import React from "react"
import { QueryClient } from "react-query"

import DayJsUtils from "@date-io/dayjs"
import { Global, ThemeProvider } from "@emotion/react"
import { StylesProvider, ThemeProvider as MuiThemeProvider } from "@material-ui/core"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"
import { HelmetProvider } from "react-helmet-async"
import { BrowserRouter as Router, StaticRouter } from "react-router-dom"

import { ClientVersionHeader, NcsApiProvider } from "@ncs/ncs-api"
import { ToastProvider, UrlStateProvider } from "@ncs/web-legos"
import { CrashErrorPage } from "~/shared-components/error-pages"

import { fonts, globalStyles, reset, theme } from "~/styles"
import * as Sentry from "@sentry/react"
import { Integrations } from "@sentry/tracing"
import { ShopContextProvider } from "./ShopContext"

declare global {
	interface Window {
		sentry?: any
		__SENTRY__?: any
	}
}

if (typeof window !== "undefined") {
	Sentry.init({
		enabled: !!import.meta.env.VITE_SENTRY_DSN,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [new Integrations.BrowserTracing()],
		tracesSampleRate: 0.1,
		release: import.meta.env.VITE_RELEASE_VERSION,
	})
	// eslint-disable-next-line no-console
	console.log(`Sentry Release Version : ${import.meta.env.VITE_RELEASE_VERSION}`)
	window.sentry = { ...window.__SENTRY__ }
}

interface AppProviderProps {
	children: React.ReactNode
	location?: string // For SSR
	queryClient?: QueryClient
}

export const AppProvider: React.FC<AppProviderProps> = ({ children, location, queryClient }) => {
	// Use StaticRouter for SSR, BrowserRouter for client
	const routerProps = typeof window !== "undefined" ? {} : { location: location || "/" }

	return (
		<HelmetProvider>
			<NcsApiProvider
				clientVersionHeader={ClientVersionHeader.Customer}
				queryClient={queryClient}
			>
				<MuiThemeProvider theme={theme}>
					<ThemeProvider theme={theme}>
						<MuiPickersUtilsProvider utils={DayJsUtils}>
							<Global styles={reset} />
							<Global styles={globalStyles} />
							<StylesProvider injectFirst>
								<Sentry.ErrorBoundary fallback={<CrashErrorPage />}>
									{typeof window !== "undefined" ?
										<Router {...routerProps}>
											<UrlStateProvider>
												<ToastProvider>
													<ShopContextProvider>
														{children}
													</ShopContextProvider>
												</ToastProvider>
											</UrlStateProvider>
										</Router>
									:	<StaticRouter {...routerProps}>
											<UrlStateProvider>
												<ToastProvider>
													<ShopContextProvider>
														{children}
													</ShopContextProvider>
												</ToastProvider>
											</UrlStateProvider>
										</StaticRouter>
									}
								</Sentry.ErrorBoundary>
							</StylesProvider>
							<Global styles={fonts} />
						</MuiPickersUtilsProvider>
					</ThemeProvider>
				</MuiThemeProvider>
			</NcsApiProvider>
		</HelmetProvider>
	)
}
